import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Claim, PartAClaim, PartBDMEClaim, ClaimDetailsRequest, Component } from '@cms-max/medicare-api-sdk'
import { createAndSanitizeMarkup } from 'utils/sanitizer'
import { useClaimTypeDisplay } from 'hooks/useClaimTypeDisplay'
import { OrderMSNModal } from '../components/OrderMSNModal/OrderMSNModal'
import { useDispatch, useSelector } from 'react-redux'
import { getModalType, setModal, closeModal } from 'redux/reducers/modal'
import { ClaimsModalTypes } from 'models/Modal'
import { Button } from 'components/Button/Button'
import sanitizeHtml from 'sanitize-html'
import { ClaimDetailsField } from './ClaimDetailsField'

export interface ClaimInformationProps {
  claim:Claim|undefined,
  request:ClaimDetailsRequest
}

const sanitizeAddressHtml = (address: string) => {
  return sanitizeHtml(address, {
    allowedTags: ['br']
  })
}

export const ClaimInformation: FC<ClaimInformationProps> = ({ claim, request }) => {
  const { t } = useTranslation('claimsSearch', { keyPrefix: 'claimDetails' })
  const dispatch = useDispatch()
  const modalTypeOpen = useSelector(getModalType)
  const openModal = (modalType: ClaimsModalTypes) => dispatch(setModal({ modalType }))
  const handleClose = () => dispatch(closeModal())
  const claimMessages = claim?.claimMessage ? createAndSanitizeMarkup(claim?.claimMessage) : undefined
  const claimTypeDisplay = useClaimTypeDisplay((claim?.claimType || ''))

  // Format and order determined by information provided in 20220511-claim-information-layout.md
  return (
    <>
      <h2 className='ds-text-heading--2xl ds-u-margin-y--0'>
        {t('information.claimDetailsSubheading')}
      </h2>
      {modalTypeOpen === 'claims-order-msn' && (
        <OrderMSNModal onClose={handleClose} request={request} segmentInd={claim?.businessSegmentIndicator || ''} />
      )}
      <div className='m-c-card ds-u-padding--3 my-u-mt--8'>
        <div className='ds-l-row ds-u-margin-bottom--2'>
          <div className='ds-l-lg-col'>
            {claim?.claimType && (
              <ClaimDetailsField
                label={t('information.claimType')}
                value={claimTypeDisplay}
              />
            )}
            {claim?.displayClaimNumber && (
              <ClaimDetailsField
                label={t('information.claimNumber')}
                value={claim.displayClaimNumber}
              />
            )}
            {claim?.startDate && (
              <ClaimDetailsField
                label={t('information.startDate')}
                value={claim.startDate}
              />
            )}
            {claim?.endDate && (
              <ClaimDetailsField
                label={t('information.endDate')}
                value={claim.endDate}
              />
            )}
          </div>
          <div className='ds-l-lg-col'>
            {(claim as PartBDMEClaim)?.receivedDate && (
              <ClaimDetailsField
                label={t('information.receivedDate')}
                value={(claim as PartBDMEClaim).receivedDate}
              />
            )}
            {(claim as PartBDMEClaim)?.processedDate && (
              <ClaimDetailsField
                label={t('information.processedDate')}
                value={(claim as PartBDMEClaim).processedDate}
              />
            )}
            {(claim as PartBDMEClaim)?.assigned && (
              <ClaimDetailsField
                label={t('information.assigned')}
                value={(claim as PartBDMEClaim).assigned}
              />
            )}
            {(claim as PartAClaim)?.admissionDate && (
              <ClaimDetailsField
                label={t('information.admissionDate')}
                value={(claim as PartAClaim).admissionDate}
              />
            )}
            {(claim as PartAClaim)?.admissionType && (
              <ClaimDetailsField
                label={t('information.admissionType')}
                value={(claim as PartAClaim).admissionType}
              />
            )}
            {((claim as PartAClaim)?.benefitDaysUsed || (claim as PartAClaim)?.benefitDaysUsed === 0) && (
              <ClaimDetailsField
                label={t('information.benefitDaysUsed')}
                value={(claim as PartAClaim).benefitDaysUsed}
              />
            )}
            {((claim as PartAClaim)?.homeHealthTotalVisits || (claim as PartAClaim)?.homeHealthTotalVisits === 0) && (
              <ClaimDetailsField
                label={t('information.homeHealthTotalVisits')}
                value={(claim as PartAClaim).homeHealthTotalVisits}
              />
            )}
            {(claim as PartAClaim)?.hospiceStartDate && (
              <ClaimDetailsField
                label={t('information.hospiceStartDate')}
                value={(claim as PartAClaim).hospiceStartDate}
              />
            )}
            {(claim as PartAClaim)?.hospiceThroughDate && (
              <ClaimDetailsField
                label={t('information.hospiceThroughDate')}
                value={(claim as PartAClaim).hospiceThroughDate}
              />
            )}
          </div>
          <div className='ds-l-md-col--6'>
            {(claim as PartBDMEClaim)?.referringProviderName && (
              <ClaimDetailsField
                label={t('information.referringProviderName')}
                value={(claim as PartBDMEClaim).referringProviderName}
              />
            )}
            {(claim as PartAClaim)?.operatingPhysicianName && (
              <ClaimDetailsField
                label={t('information.operatingPhysicianName')}
                value={(claim as PartAClaim).operatingPhysicianName}
              />
            )}
            {(claim as PartAClaim)?.attendingPhysicianName && (
              <ClaimDetailsField
                label={t('information.attendingPhysicianName')}
                value={(claim as PartAClaim).attendingPhysicianName}
              />
            )}
            {(claim as PartAClaim)?.otherPhysicianName && (
              <ClaimDetailsField
                label={t('information.otherPhysicianName')}
                value={(claim as PartAClaim).otherPhysicianName}
              />
            )}
            {claim?.providerBillingAddress && (
              <ClaimDetailsField
                label={t('information.providerBillingAddress')}
                value={{ __html: sanitizeAddressHtml(claim.providerBillingAddress) }}
              />
            )}
            {claim?.providerStreetAddress && (
              <ClaimDetailsField
                label={t('information.providerStreetAddress')}
                value={{ __html: sanitizeAddressHtml(claim.providerStreetAddress) }}
              />
            )}
            <div className='ds-u-margin-top--3 ds-u-visibility--hidden-print'>
              <Button
                className='ds-c-button ds-c-button--ghost ds-u-padding--0'
                onClick={() => openModal('claims-order-msn')}
                siteActivityProps={{
                  core: {
                    component: Component.Claims,
                    elementId: 'claims_openorderMSN_click',
                    coreClickData: {
                      linkText: t('information.orderMSNLink')
                    }
                  }
                }}
              >
                {t('information.orderMSNLink')}
              </Button>
            </div>
          </div>
        </div>
        {claimMessages && (
          <>
            <div className='ds-l-row'>
              <div className='ds-l-col--12'>
                <div className="ds-c-alert ds-c-alert--hide-icon">
                  <div className="ds-c-alert__body">
                    <h2 className="ds-c-alert__heading">{t('information.claimMessage')}</h2>
                    <p className="ds-c-alert__text" dangerouslySetInnerHTML={claimMessages}>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
